"use client";

import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ArrowRight, ArrowLeft, Check, Building2, Briefcase, DollarSign, FileText, ShieldCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
function FundingFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const {
    trigger
  } = useFormContext();
  const [activeTab, setActiveTab] = useState("contact");
  const [completedTabs, setCompletedTabs] = useState<string[]>([]);
  const steps = [{
    id: "contact",
    label: "Contact Details",
    icon: Briefcase
  }, {
    id: "asset",
    label: "Asset Class",
    icon: Building2
  }, {
    id: "financials",
    label: "Financials",
    icon: DollarSign
  }];
  const handleNext = async (currentTab: string, nextTab: string, fieldsToValidate: string[]) => {
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setCompletedTabs(prev => [...new Set([...prev, currentTab])]);
      setActiveTab(nextTab);
      const formElement = document.getElementById("funding-form-container");
      if (formElement) {
        formElement.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }
  };
  const handleTabChange = async (value: string) => {
    const currentIndex = steps.findIndex(s => s.id === activeTab);
    const targetIndex = steps.findIndex(s => s.id === value);
    if (targetIndex < currentIndex) {
      setActiveTab(value);
      return;
    }
    let fieldsToValidate: string[] = [];
    if (activeTab === "contact") fieldsToValidate = ["name", "email"];
    if (activeTab === "asset") fieldsToValidate = ["funding_type"];
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setActiveTab(value);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center animate-in fade-in duration-500">
        <div className="mb-6 rounded-full bg-primary/10 p-6 border border-primary/20">
          <ShieldCheck className="size-12 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-medium mb-2 text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage || "Your funding request has been securely transmitted to our underwriting team. A capital advisor will review your file and contact you shortly."}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()} className="min-w-[200px]">
          Submit Another Request
        </Button>
      </div>;
  }
  return <div id="funding-form-container" className="w-full">
      <Form onSubmit={handleSubmit} className="space-y-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="mb-8 overflow-x-auto pb-2">
            <TabsList className="w-full justify-start bg-transparent p-0 gap-2 md:gap-4 h-auto flex-nowrap">
              {steps.map((step, i) => {
              const Icon = step.icon;
              const isActive = activeTab === step.id;
              const isCompleted = completedTabs.includes(step.id);
              return <TabsTrigger key={step.id} value={step.id} data-index={i} className={`
                      relative flex-1 h-auto py-4 px-4 flex flex-col md:flex-row items-center gap-3 
                      border border-border bg-card/30 data-[state=active]:bg-card data-[state=active]:border-primary/50
                      transition-all duration-300 min-w-[140px]
                    `}>
                    <div className={`
                      flex items-center justify-center size-8 rounded-full border 
                      ${isActive ? 'bg-primary text-primary-foreground border-primary' : isCompleted ? 'bg-primary/20 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border'}
                    `}>
                      {isCompleted && !isActive ? <Check className="size-4" /> : <Icon className="size-4" />}
                    </div>
                    <div className="flex flex-col items-center md:items-start">
                      <span className={`text-xs uppercase tracking-wider font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                        Step 0{i + 1}
                      </span>
                      <span className={`font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {step.label}
                      </span>
                    </div>
                    
                    {}
                    {isActive && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary animate-in fade-in zoom-in duration-300" />}
                  </TabsTrigger>;
            })}
            </TabsList>
          </div>

          {}
          <TabsContent value="contact" className="space-y-6 animate-in slide-in-from-right-4 duration-300 fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="name" rules={{
              required: "Full name is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              
              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john.doe@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                    <FormLabel>Phone Number <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 123-4567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="company_name" render={({
              field
            }) => <FormItem>
                    <FormLabel>Company / Organization <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
                    <FormControl>
                      <Input placeholder="Dynestate Holdings LLC" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="flex justify-end pt-4">
              <Button type="button" onClick={() => handleNext("contact", "asset", ["name", "email"])} className="w-full md:w-auto">
                Next Step <ArrowRight className="ml-2 size-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="asset" className="space-y-6 animate-in slide-in-from-right-4 duration-300 fade-in">
            <div className="space-y-6">
              <FormField control={control} name="funding_type" rules={{
              required: "Please select a funding category"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Funding Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select a funding category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Real Estate Capital">Real Estate Capital</SelectItem>
                        <SelectItem value="Business Capital Funding">Business Capital Funding</SelectItem>
                        <SelectItem value="Credit & Financing Protection">Credit & Financing Protection</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select the primary category that best describes your capital needs.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <div className="p-4 rounded-md bg-muted/30 border border-border text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <Building2 className="size-5 text-primary mt-0.5 shrink-0" />
                  <p>
                    Dynestate specializes in complex capital structuring. Selecting the correct category ensures your request is routed to the appropriate underwriting desk immediately.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4 gap-4">
              <Button type="button" variant="outline" onClick={() => setActiveTab("contact")} className="w-full md:w-auto">
                <ArrowLeft className="mr-2 size-4" /> Back
              </Button>
              <Button type="button" onClick={() => handleNext("asset", "financials", ["funding_type"])} className="w-full md:w-auto">
                Next Step <ArrowRight className="ml-2 size-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="financials" className="space-y-6 animate-in slide-in-from-right-4 duration-300 fade-in">
            <div className="space-y-6">
              <FormField control={control} name="funding_amount" rules={{
              required: "Funding amount is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Requested Amount (USD)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input type="number" placeholder="500,000" className="pl-8" {...field} />
                      </div>
                    </FormControl>
                    <FormDescription>
                      Enter the total capital amount you are looking to secure.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="message" render={({
              field
            }) => <FormItem>
                    <FormLabel>Deal Context & Additional Details</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Provide any extra information about your project, timeline, or specific financing requirements..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-2">
                <span className="font-bold">Error:</span> {submitError}
              </div>}

            <div className="flex justify-between pt-4 gap-4">
              <Button type="button" variant="outline" onClick={() => setActiveTab("asset")} className="w-full md:w-auto">
                <ArrowLeft className="mr-2 size-4" /> Back
              </Button>
              <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px]">
                {isSubmitting ? <>
                    <Loader className="mr-2 size-4 animate-spin" />
                    Processing...
                  </> : <>
                    Submit Request <FileText className="ml-2 size-4" />
                  </>}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </Form>
    </div>;
}
export default function FundingRequestCenter() {
  return <section id="funding-request-center" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-background/90 z-10" />
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562991/img/finance_skyline_dark.png)] bg-cover bg-center opacity-30 grayscale mix-blend-overlay" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent z-20" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent z-20" />
      </div>

      <div className="container relative z-20 mx-auto px-4 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div className="lg:col-span-4 space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-foreground">
                Funding Request Center
              </h2>
              <div className="h-1 w-12 bg-primary/50" />
              <p className="text-muted-foreground leading-relaxed">
                Secure institutional-grade capital. Complete the structured request to initiate a strategic review with our underwriting team.
              </p>
            </div>

            <div className="hidden lg:block space-y-6 pt-8 border-t border-border/50">
              <div className="flex items-start gap-4">
                <div className="size-10 rounded-full bg-muted flex items-center justify-center shrink-0 border border-white/5">
                  <ShieldCheck className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground text-sm uppercase tracking-wide">Secure Transmission</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    All data is encrypted and handled with strict confidentiality protocols.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="size-10 rounded-full bg-muted flex items-center justify-center shrink-0 border border-white/5">
                  <Building2 className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground text-sm uppercase tracking-wide">Institutional Access</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Direct routing to Real Estate, Corporate Finance, and Private Credit desks.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="bg-card/50 backdrop-blur-md border border-white/10 p-6 md:p-8 rounded-lg shadow-2xl relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 size-4 border-t border-l border-primary/30" />
              <div className="absolute top-0 right-0 size-4 border-t border-r border-primary/30" />
              <div className="absolute bottom-0 left-0 size-4 border-b border-l border-primary/30" />
              <div className="absolute bottom-0 right-0 size-4 border-b border-r border-primary/30" />

              <FormProvider formId="funding_request_center_contact" sectionName="funding-request-center" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company_name: "",
              funding_amount: "",
              funding_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              funding_amount: {
                required: true
              },
              funding_type: {
                required: true
              }
            }} submitText="Submit Request" successMessage="Your funding request has been successfully submitted. Our team will review your details and contact you shortly.">
                <FundingFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}